import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { withRetry } from '../utils/utils';
import { config } from '../config';
import axios from 'axios';
import { WatchlistLabel } from '../utils/constants';

interface RSI {
    rsi: number;
    close: number;
    date: string;
}

interface TodoistTask {
    symbol: string;
    watchlist: WatchlistLabel;
    maxPrice: number;
    trailingStop: number;
    trailingStopAfterTarget: number;
    previousPrice: number;
    currentPrice: number;
    RSIs: Array<number>;
    level: number;
    EMAlevel: number;
    latestChangePC: number;
    initialRSI: RSI;
    tradingRSI: RSI;
    analysisResult: 'BUY' | 'SELL' | 'HALF BUY/WAIT' | 'HALF SELL/WAIT' | '',
    sector: string;
    tradingDate: string;
}

export interface AlpacaPosition {
    asset_id: string;
    symbol: string;
    exchange: string; // NASDAQ
    asset_class: string; // us_equity
    asset_marginable: boolean;
    qty: string; // 60
    avg_entry_price: string; // 15.67
    side: string; // long || short
    market_value: string; // 1109.4
    cost_basis: string; // "940.2"
    unrealized_pl: string; // "169.2"
    unrealized_plpc: string; // "0.1799617102744097"
    unrealized_intraday_pl: string; // "67.2"
    unrealized_intraday_plpc: string; // "0.0644789867587795"
    current_price: string; // "18.49"
    lastday_price: string; // "17.37"
    change_today: string; // "0.0644789867587795"
    qty_available: string; // "60"
    targetPercentage: number;
}

export interface AlpacaOrder {
    id: string;
    activity_type: 'FILL';
    transaction_time: string;
    type: 'fill',
    price: string;
    qty: string;
    side: 'buy' | 'sell';
    symbol:string;
    leaves_qty: string;
    order_id: string;
    cum_qty: string;
    order_status: 'filled' | 'partially_filled';
}

export interface AlpacaAccount {
    id: string;
    admin_configurations: object;
    user_configurations: object;
    account_number: string;
    status: string; // ACTIVE || INACTIVE
    crypto_status: string; // ACTIVE || INACTIVE
    options_approved_level: number;
    options_trading_level: number;
    currency: string; // USD
    buying_power: string;
    regt_buying_power: string;
    daytrading_buying_power: string;
    effective_buying_power: string;
    non_marginable_buying_power: string
    options_buying_power: string;
    bod_dtbp: string;
    cash: string;
    accrued_fees: string;
    pending_transfer_in: string;
    portfolio_value: string;
    pattern_day_trader: boolean;
    trading_blocked: boolean;
    transfers_blocked: boolean;
    account_blocked: boolean;
    created_at: string;
    trade_suspended_by_user: boolean;
    multiplier: string;
    shorting_enabled: boolean;
    equity: string;
    last_equity: string;
    long_market_value: string;
    short_market_value: string;
    position_market_value: string;
    initial_margin: string;
    maintenance_margin: string;
    last_maintenance_margin: string;
    sma: string;
    daytrade_count: number;
    balance_asof: string;
    crypto_tier: number;
};

export interface PortfolioHistory {
    timestamp: Array<number>;
    equity: Array<number>;
    profit_loss: Array<number>;
    profit_loss_pct: Array<number>;
    base_value: number;
    base_value_asof: string;
    timeframe: string;
}

export interface PortfolioHistoryParams {
    period: string; // 1D, 1W, 1M, 6M, 1Y, 5Y
    timeframe: string; // 5Min, 1H, 1D, 1D, 1D, 1D
    intraday_reporting: string;
    pnl_reset: string;
    startDate: string;
    endDate: string;
}

export interface PositionsState {
    positions: AlpacaPosition[];
    orders: AlpacaOrder[];
    account: AlpacaAccount | null;
    history: PortfolioHistory | null;
    oneDay: PortfolioHistory | null;
    loading: boolean;
    error: string | null;
}

// Initial state
const initialState: PositionsState = {
    positions: [],
    orders: [],
    account: null,
    history: null,
    oneDay: null,
    loading: false,
    error: null,
}
const stocksToSkip = new Set(['PXD', 'ERF', 'SMAR', 'AZPN']);

export const fetchPositions = createAsyncThunk<AlpacaPosition[], void, { rejectValue: string }>(
    'positions/fetchPositions',
    async (_, thunkAPI) => {
        try {
            const positions = await withRetry(async () => {
                const result = await axios({
                    baseURL: config.alpaca.baseUrl,
                    method: 'get',
                    url: '/v2/positions',
                    headers: {
                        'APCA-API-KEY-ID': config.alpaca.keyId,
                        'APCA-API-SECRET-KEY': config.alpaca.secretKey,
                        'accept': 'application/json'
                    }
                });
                /* const todoistTasksResult = await axios({
                    baseURL: config.todoist.baseUrl,
                    method: 'get',
                    url: '/v2/tasks',
                    data: {project_id: config.todoist.projectId},
                    headers: {
                        'Authorization': `Bearer ${config.todoist.accessToken}`,
                        'accept': 'application/json'
                    }
                });

                const todoistTasks = todoistTasksResult.data.map(d => JSON.parse(d.description));
                const positions = result.data.filter(s => !stocksToSkip.has(s.symbol.toUpperCase()));
                return positions.map(p => {
                    const task = todoistTasks.find(t => t.symbol === p.symbol);
                    if (task) {
                        return { ...p, ...task, tradingDate: task.initialRSI.date };
                    }
                    return null;
                }).filter(s => s !== null);*/
                return result.data;
            }, 2);

            return positions;
        } catch (error) {
            return thunkAPI.rejectWithValue('Network error occurred');
        }
    }
);

export const fetchAccount = createAsyncThunk<AlpacaAccount, void, { rejectValue: string }>(
    'positions/fetchAccount',
    async (_, thunkAPI) => {
        try {
            const account = await withRetry(async () => {
                const result = await axios({
                    baseURL: config.alpaca.baseUrl,
                    method: 'get',
                    url: '/v2/account',
                    headers: {
                        'APCA-API-KEY-ID': config.alpaca.keyId,
                        'APCA-API-SECRET-KEY': config.alpaca.secretKey,
                        'accept': 'application/json'
                    }
                  });
                return result.data;
            }, 2);

            return account;
        } catch (error) {
            return thunkAPI.rejectWithValue('Network error occurred');
        }
    }
);

export const fetchHistory = createAsyncThunk<PortfolioHistory, PortfolioHistoryParams, { rejectValue: string }>(
    'positions/fetchHistory',
    async (params, thunkAPI) => {
        try {
            const history = await withRetry(async () => {
                const result = await axios({
                    baseURL: config.alpaca.baseUrl,
                    method: 'get',
                    url: `/v2/account/portfolio/history?timeframe=${params.timeframe}&intraday_reporting=${params.intraday_reporting}&pnl_reset=${params.pnl_reset}${params.period === 'YTD' ? `&start=${params.startDate}&end=${params.endDate}`: `&period=${params.period}`}`,
                    // url: `/v2/account/portfolio/history?timeframe=${params.timeframe}&intraday_reporting=${params.intraday_reporting}&pnl_reset=${params.pnl_reset}&start=${params.startDate}&end=${params.endDate}`,
                    headers: {
                        'APCA-API-KEY-ID': config.alpaca.keyId,
                        'APCA-API-SECRET-KEY': config.alpaca.secretKey,
                        'accept': 'application/json'
                    }
                });
                
                return result.data;
            }, 2);

            history.timestamp = history.timestamp.slice(0, history.timestamp.length - 1);
            history.equity = history.equity.slice(0, history.equity.length - 1);
            history.profit_loss = history.profit_loss.slice(0, history.profit_loss.length - 1);
            history.profit_loss_pct = history.profit_loss_pct.slice(0, history.profit_loss_pct.length - 1);
            return history;
        } catch (error) {
            return thunkAPI.rejectWithValue('Network error occurred');
        }
    }
);

export const fetchOrders = createAsyncThunk<AlpacaOrder[], string, { rejectValue: string }>(
    'positions/fetchOrders',
    async (ordersAfterDate, thunkAPI) => {
        try {
            const totalOrders: AlpacaOrder[] = [];
            const getOrders = async (lastId?: string) => {
                const orders = await withRetry(async () => {
                    const result = await axios({
                        baseURL: config.alpaca.baseUrl,
                        method: 'get',
                        url: `v2/account/activities?activity_types=FILL&after=${ordersAfterDate}&direction=desc&page_size=100${lastId ? `&page_token=${lastId}` : ''}`,
                        headers: {
                            'APCA-API-KEY-ID': config.alpaca.keyId,
                            'APCA-API-SECRET-KEY': config.alpaca.secretKey,
                            'accept': 'application/json'
                        }
                    });
                    return result.data;
                }, 2);

                if (orders.length) {
                    totalOrders.push(...orders);
                    await getOrders(orders[orders.length - 1].id);
                }
            }
            await getOrders();

            return totalOrders;
        } catch (error) {
            return thunkAPI.rejectWithValue('Network error occurred');
        }
    }
);

export const positionSlice = createSlice({
    name: 'position',
    initialState,
    reducers: {
    },
    extraReducers: (builder) => {
        builder.addCase(fetchPositions.fulfilled, (state, action) => {
            state.loading = false;
            state.positions = action.payload;
        });
        builder.addCase(fetchAccount.fulfilled, (state, action) => {
            state.loading = false;
            state.account = action.payload;
        });
        builder.addCase(fetchHistory.fulfilled, (state, action) => {
            state.loading = false;
            state.history = action.payload;
            if (action.meta.arg.period === '1D') {
                state.oneDay = action.payload;
            } else if (state.oneDay) {
                state.history.timestamp = [...action.payload.timestamp, state.oneDay.timestamp[state.oneDay.timestamp.length - 1]];
                state.history.equity = [...action.payload.equity, state.oneDay.equity[state.oneDay.equity.length - 1]];
                state.history.profit_loss = [...action.payload.profit_loss, state.oneDay.profit_loss[state.oneDay.profit_loss.length - 1]];
                state.history.profit_loss_pct = [...action.payload.profit_loss_pct, state.oneDay.profit_loss_pct[state.oneDay.profit_loss_pct.length - 1]];
            }
        });
        builder.addCase(fetchOrders.fulfilled, (state, action) => {
            state.loading = false;
            state.orders = action.payload;
        });
        builder.addMatcher(isAnyOf(fetchPositions.pending, fetchAccount.pending, fetchHistory.pending, fetchOrders.pending), (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addMatcher(isAnyOf(fetchPositions.rejected, fetchAccount.rejected, fetchHistory.rejected, fetchOrders.rejected), (state, action) => {
            state.loading = false;
            state.error = action.payload || 'Something went wrong';
        });
    },
});
